import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { AllServicesService } from '../../../services/all-services.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import { startWith, map, Subject, takeUntil, filter, distinctUntilChanged, firstValueFrom, take } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedServiceService } from '../../services/shared-service.service';
import { LoggerService } from '../../services/logger.service';
import { NotificationFacadeService } from '../../services/notification-facade.service';
import { mapUnknownError } from '../../services/app-error-catalog';

@Component({
  selector: 'app-bc-fob-m-package',
  imports: [CommonModule, FormsModule, ScrollingModule,
    MatFormFieldModule, MatSelectModule, MatInputModule,
    MatAutocompleteModule, ReactiveFormsModule],
  providers: [FormBuilder],
  templateUrl: './bc-fob-m-package.component.html',
  styleUrl: './bc-fob-m-package.component.scss'
})

/**
 * Package selector and treat-weight editor used in the modification panel.
 *
 * This component keeps its current inline validation behavior, but operational
 * failures such as package retrieval or dosage adjustment are now routed through
 * the shared frontend error infrastructure.
 */
export class BcFobMPackageComponent implements OnInit, OnChanges {
  @Input() packageData: any = []; // Receive data from the parent


  form!: FormGroup;

  packageControl1 = new FormControl();
  packageControl2 = new FormControl();
  treat1P1Control = new FormControl();
  treat2P1Control = new FormControl();
  treat1P2Control = new FormControl();
  treat2P2Control = new FormControl();

  filteredPackages1: any[] = [];
  filteredPackages2: any[] = [];

  // Variables to store the last valid selected packages
  lastValidPackage1: any = null;
  lastValidPackage2: any = null;

  retrievedData: any[] = [];

  selectedValue1: string = '';
  selectedValue2: string = '';
  tempData: any[] = [
    { Fixed_value: "Package 1", Name: null, Batch: null, kv100_cSt: null, Vi: null, Treat_wt_ratio_P1: null, Treat_wt_P1: null, Treat_wt_ratio_P2: null, Treat_wt_P2: null },
    { Fixed_value: "Package 2", Name: null, Batch: null, kv100_cSt: null, Vi: null, Treat_wt_ratio_P1: null, Treat_wt_P1: null, Treat_wt_ratio_P2: null, Treat_wt_P2: null },
  ];

  constructor(
    private fb: FormBuilder,
    private dataService: AllServicesService,
    private sharedService: SharedServiceService,
    private logger: LoggerService,
    private notificationFacade: NotificationFacadeService) {
  }

  private destroy$ = new Subject<void>();
  private isSyncingFromPackages = false;

  // Formatted treat value captured on focus; only one input is focused at a time.
  private treatValueOnFocus: string | null = null;

  /**
   * Normalizes user-triggered operational failures so package selection and dosage
   * adjustment behave consistently with the rest of the error-handling branch.
   */
  private notifyOperationError(error: unknown, source: string, context?: Record<string, unknown>): void {
    const appError = mapUnknownError(error, source);
    this.logger.error(appError, context);
    this.notificationFacade.showError(appError);
  }

  private formatToTwoDecimals(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2);
  }

  // Display value for a package treat total: 0.00 when the package is selected, blank otherwise.
  private treatDisplayForTotal(total: unknown, packageIndex: number): string | null {
    const isZero = total === 0 || total === '0' || Number(total) === 0;
    if (isZero) {
      const hasPackage = packageIndex === 0
        ? this.isValidPackageObject(this.packageControl1.value)
        : this.isValidPackageObject(this.packageControl2.value);
      return hasPackage ? '0.00' : null;
    }
    return this.formatToTwoDecimals(total);
  }

  private sanitizeNumericInputValue(value: string): string {
    if (!value) {
      return '';
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) {
      return cleaned;
    }
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }

  restrictNumericInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab', 'Enter', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleTreatInput(event: Event, packageIndex: number, blendIndex: number): void {
    const target = event.target as HTMLInputElement;
    const cleaned = this.sanitizeNumericInputValue(target.value);
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
    const control = this.getTreatControl(packageIndex, blendIndex);
    control.setValue(cleaned, { emitEvent: false });
  }

  onTreatFocus(packageIndex: number, blendIndex: number): void {
    const control = this.getTreatControl(packageIndex, blendIndex);
    this.treatValueOnFocus = this.formatToTwoDecimals(control.value);
  }

  onTreatBlur(packageIndex: number, blendIndex: number): void {
    const control = this.getTreatControl(packageIndex, blendIndex);
    const formatted = this.formatToTwoDecimals(control.value);
    const valueOnFocus = this.treatValueOnFocus;
    this.treatValueOnFocus = null;

    // Verify if there is a selected package for this index
    const hasPackage = packageIndex === 0
      ? this.isValidPackageObject(this.packageControl1.value)
      : this.isValidPackageObject(this.packageControl2.value);

    // Show 0.00 for an empty treat when a package is selected (empty already behaves as 0).
    const emptyDisplay = hasPackage ? '0.00' : null;

    // Value unchanged during focus: re-normalize display only, skip dosage adjustment to avoid drift.
    if (formatted === valueOnFocus) {
      control.setValue(formatted === '' ? emptyDisplay : formatted, { emitEvent: false });
      this.calculateSums();
      return;
    }

    if (formatted === '') {
      control.setValue(emptyDisplay, { emitEvent: false });

      // If there is a package but the input is cleared, treat it as 0 and update dosages; 
      // if there is no package, just clear the input without affecting dosages
      if (hasPackage) {
        if (blendIndex === 0) {
          this.tempData[packageIndex].Treat_wt_P1 = 0;
        } else {
          this.tempData[packageIndex].Treat_wt_P2 = 0;
        }
        this.onTreatWeightChange('0', packageIndex, blendIndex);
      } else {
        // Just recalculate sums locally without calling the weight change logic, 
        // since there's no package selected to adjust 
        this.calculateSums();
      }
      return;
    }

    control.setValue(formatted, { emitEvent: false });
    if (blendIndex === 0) {
      this.tempData[packageIndex].Treat_wt_P1 = formatted;
    } else {
      this.tempData[packageIndex].Treat_wt_P2 = formatted;
    }
    this.onTreatWeightChange(formatted, packageIndex, blendIndex);
  }

  private getTreatControl(packageIndex: number, blendIndex: number): FormControl {
    if (packageIndex === 0 && blendIndex === 0) {
      return this.treat1P1Control;
    }
    if (packageIndex === 0 && blendIndex === 1) {
      return this.treat2P1Control;
    }
    if (packageIndex === 1 && blendIndex === 0) {
      return this.treat1P2Control;
    }
    return this.treat2P2Control;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      packageControl1: this.packageControl1,
      packageControl2: this.packageControl2,
      treat1P1Control: this.treat1P1Control,
      treat2P1Control: this.treat2P1Control,
      treat1P2Control: this.treat1P2Control,
      treat2P2Control: this.treat2P2Control
    });

    // Initialize both controls and lists
    this.filteredPackages1 = this.packageData;
    this.filteredPackages2 = this.packageData;

    this.packageControl1.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.Package),
      map(name => name ? this._filter(name) : this.packageData.slice())
    ).subscribe(filtered => this.filteredPackages1 = filtered);

    this.packageControl2.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.Package),
      map(name => name ? this._filter(name) : this.packageData.slice())
    ).subscribe(filtered => this.filteredPackages2 = filtered);

    // Subscribe to changes to save valid selections
    this.packageControl1.valueChanges.subscribe(value => {
      if (this.isValidPackageObject(value)) {
        this.lastValidPackage1 = value;
      }
    });

    this.packageControl2.valueChanges.subscribe(value => {
      if (this.isValidPackageObject(value)) {
        this.lastValidPackage2 = value;
      }
    });

    // Hydrate package selections when a favorite is imported
    this.sharedService.ImpFavoriteFlag$
      .pipe(
        takeUntil(this.destroy$),
        filter(flag => flag === true)
      )
      .subscribe(() => this.hydratePackagesFromImport());

    // Synchronize Treat Wt% when totals change from the components table (Packages)
    // Convention:
    // - SharedService observables: total_package_{PKG}_pred_{BLEND} donde PKG=1-2, PRED=1-2
    // - HTML Controls: treat{BLEND}P{PKG}Control donde BLEND=1-2, PKG=1-2
    // 
    // Visual structure:
    //              Blend 1              Blend 2
    // Package 1:   treat1P1Control      treat2P1Control
    // Package 2:   treat1P2Control      treat2P2Control
    //
    // Correct mapping:
    // total_package_1_pred_1 (Pkg1, Blend1) -> treat1P1Control, tempData[0].Treat_wt_P1
    // total_package_1_pred_2 (Pkg1, Blend2) -> treat2P1Control, tempData[0].Treat_wt_P2
    // total_package_2_pred_1 (Pkg2, Blend1) -> treat1P2Control, tempData[1].Treat_wt_P1
    // total_package_2_pred_2 (Pkg2, Blend2) -> treat2P2Control, tempData[1].Treat_wt_P2

    const eq4 = (a: any, b: any) => Number(a).toFixed(4) === Number(b).toFixed(4);

    // Package 1, Blend 1
    this.sharedService.total_package_1_pred_1$
      .pipe(
        takeUntil(this.destroy$),
        filter((v: any) => v !== null && v !== undefined),
        distinctUntilChanged(eq4)
      )
      .subscribe(total => {
        this.isSyncingFromPackages = true;
        const val = (total === 0 || total === '0') ? null : total;
        const formatted = val === null ? null : this.formatToTwoDecimals(val);
        this.treat1P1Control.setValue(this.treatDisplayForTotal(total, 0), { emitEvent: false });
        this.tempData[0].Treat_wt_P1 = formatted;
        this.calculateSums();
        this.isSyncingFromPackages = false;
      });

    // Package 1, Blend 2
    this.sharedService.total_package_1_pred_2$
      .pipe(
        takeUntil(this.destroy$),
        filter((v: any) => v !== null && v !== undefined),
        distinctUntilChanged(eq4)
      )
      .subscribe(total => {
        this.isSyncingFromPackages = true;
        const val = (total === 0 || total === '0') ? null : total;
        const formatted = val === null ? null : this.formatToTwoDecimals(val);
        this.treat2P1Control.setValue(this.treatDisplayForTotal(total, 0), { emitEvent: false });
        this.tempData[0].Treat_wt_P2 = formatted;
        this.calculateSums();
        this.isSyncingFromPackages = false;
      });

    // Package 2, Blend 1
    this.sharedService.total_package_2_pred_1$
      .pipe(
        takeUntil(this.destroy$),
        filter((v: any) => v !== null && v !== undefined),
        distinctUntilChanged(eq4)
      )
      .subscribe(total => {
        this.isSyncingFromPackages = true;
        const val = (total === 0 || total === '0') ? null : total;
        const formatted = val === null ? null : this.formatToTwoDecimals(val);
        this.treat1P2Control.setValue(this.treatDisplayForTotal(total, 1), { emitEvent: false });
        this.tempData[1].Treat_wt_P1 = formatted;
        this.calculateSums();
        this.isSyncingFromPackages = false;
      });

    // Package 2, Blend 2
    this.sharedService.total_package_2_pred_2$
      .pipe(
        takeUntil(this.destroy$),
        filter((v: any) => v !== null && v !== undefined),
        distinctUntilChanged(eq4)
      )
      .subscribe(total => {
        this.isSyncingFromPackages = true;
        const val = (total === 0 || total === '0') ? null : total;
        const formatted = val === null ? null : this.formatToTwoDecimals(val);
        this.treat2P2Control.setValue(this.treatDisplayForTotal(total, 1), { emitEvent: false });
        this.tempData[1].Treat_wt_P2 = formatted;
        this.calculateSums();
        this.isSyncingFromPackages = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void { }


  trackByFn(index: number, item: any): number {
    return item.id; // or any unique property of the object
  }

  calculateSums(): void {
    const hasPackage1 = this.isValidPackageObject(this.packageControl1.value);
    const hasPackage2 = this.isValidPackageObject(this.packageControl2.value);

    const treat1P1 = hasPackage1 ? (parseFloat(this.treat1P1Control.value) || 0) : 0;
    const treat2P1 = hasPackage1 ? (parseFloat(this.treat2P1Control.value) || 0) : 0;
    const treat1P2 = hasPackage2 ? (parseFloat(this.treat1P2Control.value) || 0) : 0;
    const treat2P2 = hasPackage2 ? (parseFloat(this.treat2P2Control.value) || 0) : 0;

    // this.totalWtP1.Treat_wt_P1 = parseFloat((treat1P1 + treat2P1).toFixed(2));
    // this.totalWtP2.Treat_wt_P2 = parseFloat((treat1P2 + treat2P2).toFixed(2));

    this.sharedService.updateComponentTotals(
      'package',
      parseFloat((treat1P1 + treat1P2).toFixed(2)),
      parseFloat((treat2P1 + treat2P2).toFixed(2))
    );
  }


  // Method called when a package is selected
  onPackageSelected(event: any, rowIndex: number): void {
    const selectedPackage = event.option.value;

    // Save the valid selection
    if (rowIndex === 0) {
      this.lastValidPackage1 = selectedPackage;
    } else if (rowIndex === 1) {
      this.lastValidPackage2 = selectedPackage;
    }
    rowIndex == 0 ? this.packageControl1.setValue(selectedPackage, { emitEvent: false }) : this.packageControl2.setValue(selectedPackage, { emitEvent: false });
    this.dataService.get_DI_data([selectedPackage.Package, rowIndex])
      .subscribe({
        next: (data) => {
          this.retrievedData[rowIndex] = [selectedPackage.Package, ...data];
          this.sharedService.setRetrievedPackageData(this.retrievedData);
          this.sharedService.setRetrievedPackageIndex(rowIndex);
          if (data && data.length > 0) {
            if (rowIndex == 0) {
              const formatted = this.formatToTwoDecimals(data[rowIndex]);
              this.treat1P1Control.setValue(formatted, { emitEvent: false });
              this.treat2P1Control.setValue(formatted, { emitEvent: false });
              this.tempData[0].Treat_wt_P1 = formatted;
              this.tempData[0].Treat_wt_P2 = formatted;
            }
            else {
              const formatted = this.formatToTwoDecimals(data[0]);
              this.treat1P2Control.setValue(formatted, { emitEvent: false });
              this.treat2P2Control.setValue(formatted, { emitEvent: false });
              this.tempData[1].Treat_wt_P1 = formatted;
              this.tempData[1].Treat_wt_P2 = formatted;
            }
            this.calculateSums();
          }
        },
        error: (error) => {
          this.notifyOperationError(error, 'onPackageSelected', {
            operation: 'onPackageSelected',
            rowIndex,
            selectedPackage: selectedPackage?.Package
          });
        }
      });
  }

  // Method that runs when the input loses focus
  onPackageInputBlur(index: number): void {
    if (index === 0) {
      const currentValue = this.packageControl1.value;
      if (!this.isValidPackageSelection(currentValue)) {
        this.packageControl1.setValue(this.lastValidPackage1, { emitEvent: false });
      }
    } else if (index === 1) {
      const currentValue = this.packageControl2.value;
      if (!this.isValidPackageSelection(currentValue)) {
        this.packageControl2.setValue(this.lastValidPackage2, { emitEvent: false });
      }
    }
  }

  // Validates whether the current value is a valid selected package
  private isValidPackageSelection(value: any): boolean {
    if (!value) return true; // Allow empty values

    // If it is an object with a Package property
    if (typeof value === 'object' && value.Package) {
      return this.packageData.some((pkg: any) => pkg.Package === value.Package);
    }

    return false; // If it is a string or another type, it is not valid
  }

  // Validates whether it is a valid package object
  private isValidPackageObject(value: any): boolean {
    return value && typeof value === 'object' && value.Package &&
      this.packageData.some((pkg: any) => pkg.Package === value.Package);
  }

  clearPackageSelection(index: number): void {
    if (index === 0) {
      this.packageControl1.setValue(null);
      this.lastValidPackage1 = null;
      this.treat1P1Control.setValue(null);
      this.treat2P1Control.setValue(null);
      this.retrievedData[0] = null;
    } else if (index === 1) {
      this.packageControl2.setValue(null);
      this.lastValidPackage2 = null;
      this.treat1P2Control.setValue(null);
      this.treat2P2Control.setValue(null);
      this.retrievedData[1] = null;
    }
    this.sharedService.setRetrievedPackageData(this.retrievedData);
    this.calculateSums();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  // In the component class
  displayFn(item: any): string {
    return item && item.Package ? item.Package : '';
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.packageData.filter((option: { Package: string }) =>
      option.Package.toLowerCase().includes(filterValue)
    );
  }

  private async getCurrentRowsAndDilPercent(
    packageIndex: number,
    blendIndex: number
  ): Promise<{ currentRows: any[]; dilPercent: number }> {
    let currentRows: any[] = [];
    let dilPercent = 0;

    if (packageIndex === 0 && blendIndex === 0) {
      currentRows = await firstValueFrom(this.sharedService.package_1_pred_1$.pipe(take(1)));
      dilPercent = await firstValueFrom(this.sharedService.total_dil_per_package_1_pred_1$.pipe(take(1)));
    } else if (packageIndex === 0 && blendIndex === 1) {
      currentRows = await firstValueFrom(this.sharedService.package_1_pred_2$.pipe(take(1)));
      dilPercent = await firstValueFrom(this.sharedService.total_dil_per_package_1_pred_2$.pipe(take(1)));
    } else if (packageIndex === 1 && blendIndex === 0) {
      currentRows = await firstValueFrom(this.sharedService.package_2_pred_1$.pipe(take(1)));
      dilPercent = await firstValueFrom(this.sharedService.total_dil_per_package_2_pred_1$.pipe(take(1)));
    } else if (packageIndex === 1 && blendIndex === 1) {
      currentRows = await firstValueFrom(this.sharedService.package_2_pred_2$.pipe(take(1)));
      dilPercent = await firstValueFrom(this.sharedService.total_dil_per_package_2_pred_2$.pipe(take(1)));
    }

    if ((!currentRows || currentRows.length === 0) && this.retrievedData?.[packageIndex]?.[3]) {
      currentRows = this.retrievedData[packageIndex][3];
    }

    if ((!dilPercent || Number(dilPercent) === 0) && this.retrievedData?.[packageIndex]) {
      const packageDil = Number(this.retrievedData[packageIndex][2]);
      if (!isNaN(packageDil) && packageDil > 0) {
        dilPercent = packageDil;
      }
    }

    return { currentRows, dilPercent };
  }

  // Add this method
  /**
   * Rebalances package dosages after a treat-weight edit.
   * Any failure here is operational because the user explicitly triggered the
   * recalculation and expects feedback if the backend adjustment fails.
   */
  async onTreatWeightChange(value: string, packageIndex: number, blendIndex: number): Promise<void> {
    const rawValue = (value ?? '').toString().trim();
    let numericValue = rawValue === '' ? 0 : parseFloat(rawValue);

    if (isNaN(numericValue)) {
      this.calculateSums();
      return;
    }

    if (numericValue < 0) {
      numericValue = 0;
    }

    // Verify whether it is greater than 100 and cap it
    if (!isNaN(numericValue) && numericValue > 100) {
      numericValue = 100;

      // Update the FormControl with the limited value
      if (packageIndex === 0 && blendIndex === 0) {
        this.treat1P1Control.setValue(numericValue, { emitEvent: false });
      } else if (packageIndex === 0 && blendIndex === 1) {
        this.treat2P1Control.setValue(numericValue, { emitEvent: false });
      } else if (packageIndex === 1 && blendIndex === 0) {
        this.treat1P2Control.setValue(numericValue, { emitEvent: false });
      } else if (packageIndex === 1 && blendIndex === 1) {
        this.treat2P2Control.setValue(numericValue, { emitEvent: false });
      }
    }

    // Update local tempData
    if (blendIndex === 0) {
      this.tempData[packageIndex].Treat_wt_P1 = numericValue;
    } else {
      this.tempData[packageIndex].Treat_wt_P2 = numericValue;
    }

    // Build payload for proportional adjustment (get_di_dosage_adjust)
    try {
      const { currentRows, dilPercent } = await this.getCurrentRowsAndDilPercent(packageIndex, blendIndex);

      // If there are no rows, we cannot adjust
      if (!currentRows || currentRows.length === 0) {
        this.calculateSums();
        return;
      }

      // Filter invalid rows and map them to the API expected format
      let normalizedRows = currentRows
        .map((r: any) => ({
          additive: (r.Additive ?? r.component ?? '').toString().trim(),
          dosage: (() => {
            const v = r.Dosage ?? r.treatRateRaw ?? r.treatRate ?? 0;
            const n = typeof v === 'string' ? parseFloat(v) : Number(v);
            return isNaN(n) ? 0 : n;
          })()
        }))
        // Backend fails if there are empty additives or 'text' markers
        .filter((r: any) => r.additive && r.additive.toLowerCase() !== 'text');

      // If we come from a previously balanced state at 0, recover the original base
      // to allow recalculating again when Treat > 0.
      if (
        numericValue > 0 &&
        !normalizedRows.some((r: any) => Number(r.dosage) > 0) &&
        this.retrievedData?.[packageIndex]?.[3]
      ) {
        const fallbackRows = this.retrievedData[packageIndex][3]
          .map((r: any) => ({
            additive: (r.Additive ?? r.component ?? '').toString().trim(),
            dosage: (() => {
              const v = r.Dosage ?? r.treatRateRaw ?? r.treatRate ?? 0;
              const n = typeof v === 'string' ? parseFloat(v) : Number(v);
              return isNaN(n) ? 0 : n;
            })()
          }))
          .filter((r: any) => r.additive && r.additive.toLowerCase() !== 'text');

        if (fallbackRows.some((r: any) => Number(r.dosage) > 0)) {
          normalizedRows = fallbackRows;
        }
      }

      if (normalizedRows.length === 0) {
        this.calculateSums();
        return;
      }

      const predictionList = normalizedRows.map(() => blendIndex + 1);
      const packageList = normalizedRows.map(() => packageIndex + 1);
      const additiveList = normalizedRows.map((r: any) => r.additive);
      const dosageList = normalizedRows.map((r: any) => r.dosage);

      if (dosageList.length === 0 || additiveList.length === 0) {
        this.calculateSums();
        return;
      }

      // Backend expects flat keys (not nested di_inp/dil_oil)
      const payload = {
        // selection context
        package: [packageIndex + 1],        // value -> package = value[-1]
        newValue: numericValue,             // new_value
        field: [blendIndex + 1],            // field -> pred_num = field[-1]

        // DI inputs (columns for DataFrame)
        pred_num: predictionList,           // → becomes 'Prediction'
        package_num: packageList,           // → becomes 'Package'
        additive: additiveList,
        dosage: dosageList,

        // Dilution inputs (also DF columns)
        pred_dil_per: [blendIndex + 1],
        package_dil_per: [packageIndex + 1],
        dil_per: [Number(dilPercent) || 0]
      };

      this.dataService.get_di_dosage_adjust(payload).subscribe({
        next: (adjusted: any[]) => {
          // Notify a package+blend-specific update so the opposite blend is not overwritten.
          // Pass the requested treat so the packages table uses it verbatim instead of a
          // recomputed sum that drifts by +/-0.01 due to per-component rounding.
          this.sharedService.setRetrievedPackageBlendUpdate(packageIndex, blendIndex, adjusted, numericValue);

          // Recalculate local sums
          this.calculateSums();
        },
        error: (err) => {
          this.notifyOperationError(err, 'onTreatWeightChange', {
            operation: 'onTreatWeightChange',
            packageIndex,
            blendIndex,
            additiveList,
            dosageList,
            predictionList,
            packageList,
            dilPercent,
            payload
          });
          this.calculateSums();
        }
      });
    } catch (e) {
      this.notifyOperationError(e, 'onTreatWeightChange', {
        operation: 'onTreatWeightChange',
        packageIndex,
        blendIndex
      });
      this.calculateSums();
    }
  }

  /**
   * Hydrate package name selections and treat weights from imported favorite data.
   * Reads finishedoil_data$ to find Package 1/2 names, matches against dropdown,
   * and programmatically triggers package selection + DI data load.
   */
  private async hydratePackagesFromImport(): Promise<void> {
    // Clear existing state before importing
    this.packageControl1.setValue(null, { emitEvent: false });
    this.packageControl2.setValue(null, { emitEvent: false });
    this.treat1P1Control.setValue(null, { emitEvent: false });
    this.treat2P1Control.setValue(null, { emitEvent: false });
    this.treat1P2Control.setValue(null, { emitEvent: false });
    this.treat2P2Control.setValue(null, { emitEvent: false });
    this.lastValidPackage1 = null;
    this.lastValidPackage2 = null;
    this.tempData = [{ Name: '', Treat_wt_P1: '', Treat_wt_P2: '' }, { Name: '', Treat_wt_P1: '', Treat_wt_P2: '' }];

    const foData = await firstValueFrom(this.sharedService.finishedoil_data$);
    if (!foData || !Array.isArray(foData) || foData.length === 0) {
      return;
    }

    // FO data rows: Package 1, Package 2, VM 1, VM 2, PPD 1, PPD 2, Base oil 1-6
    const packageRows = foData.filter((row: any) =>
      row.Fixed_value && row.Fixed_value.startsWith('Package')
    );

    // Imported DI rows and dilution already live in shared observables; read them to
    // rebuild retrievedData locally instead of re-fetching defaults from the backend.
    const importedRows = await Promise.all([
      firstValueFrom(this.sharedService.package_1_pred_1$),
      firstValueFrom(this.sharedService.package_2_pred_1$)
    ]);
    const importedDil = await Promise.all([
      firstValueFrom(this.sharedService.Dil_wt_percent_Pack1_pred1$),
      firstValueFrom(this.sharedService.Dil_wt_percent_Pack2_pred1$)
    ]);

    // Suppress the retrievedPackageData combineLatest in bc-fob-packages so it does
    // not rebuild the table with default values while we hydrate from the favorite.
    this.sharedService.setIsHydratingFavoritePackages(true);

    for (let i = 0; i < packageRows.length && i < 2; i++) {
      const row = packageRows[i];
      const packageName = row.Name;
      if (!packageName) continue;

      // Find in dropdown data
      const match = this.packageData?.find((pkg: any) => pkg.Package === packageName);
      if (!match) continue;

      // Set form control
      const control = i === 0 ? this.packageControl1 : this.packageControl2;
      control.setValue(match, { emitEvent: false });
      if (i === 0) this.lastValidPackage1 = match;
      else this.lastValidPackage2 = match;

      // Set treat weights from FO data (saved values)
      const treatP1 = row.Treat_wt_P1 ? this.formatToTwoDecimals(row.Treat_wt_P1) : null;
      const treatP2 = row.Treat_wt_P2 ? this.formatToTwoDecimals(row.Treat_wt_P2) : null;

      if (i === 0) {
        this.treat1P1Control.setValue(treatP1, { emitEvent: false });
        this.treat2P1Control.setValue(treatP2, { emitEvent: false });
        this.tempData[0].Name = packageName;
        this.tempData[0].Treat_wt_P1 = treatP1;
        this.tempData[0].Treat_wt_P2 = treatP2;
      } else {
        this.treat1P2Control.setValue(treatP1, { emitEvent: false });
        this.treat2P2Control.setValue(treatP2, { emitEvent: false });
        this.tempData[1].Name = packageName;
        this.tempData[1].Treat_wt_P1 = treatP1;
        this.tempData[1].Treat_wt_P2 = treatP2;
      }

      // Rebuild retrievedData from imported data (shape: [name, treat, dilPercent, rows])
      const rows = Array.isArray(importedRows[i]) ? importedRows[i] : [];
      const dil = Number(importedDil[i]) || 0;
      this.retrievedData[i] = [packageName, null, dil, rows];
    }

    // Publish package names so re-saving the favorite keeps them (used by serializer)
    this.sharedService.setRetrievedPackageData(this.retrievedData);
    this.calculateSums();

    // Release the suppression after the combineLatest debounce window has elapsed
    setTimeout(() => this.sharedService.setIsHydratingFavoritePackages(false), 250);
  }
}
